import { AppIcon } from '@/components/ui/app-icon';
import {
  PlanTileView,
  TILE,
  type PlanTile,
} from '@/components/upgrade/plan-tiles';
import { Button } from '@/components/ui/button';
import { UpgradeLink } from '@/components/upgrade/upgrade-link.component';
import { ComparePlansDialog } from '@/containers/onboarding/compare-plans-dialog.component';
import { TrialFooter } from '@/containers/onboarding/onboarding-footers.component';
import { iconArrowRight, iconSpinnerThird } from '@/lib/icons';
import type { UpgradeTier } from '@/lib/upgrade-link';
import { cn } from '@/lib/utils';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

/**
 * Every page a plan doesn't include, as one card in the Pro trial page's
 * look: the title, a line, what the plan brings as tiles (the ninth, when
 * there is room, opens every plan side by side), the way in, and under the
 * card a customer in their words and the rating.
 */

/** The side-by-side plans table: the plan it highlights, and whether that
 *  plan is the workspace's trial. */
export interface UpgradeCardCompare {
  plan: string;
  trial?: boolean;
}

export interface UpgradeCardAction {
  label: string;
  /** Run in place (the trial's Checkout); without it, the upgrade wizard.
   *  With `to`, it runs as the link is followed instead (e.g. analytics). */
  onClick?: () => void;
  busy?: boolean;
  /** An in-app destination other than the upgrade wizard. */
  to?: string;
  search?: Record<string, unknown>;
  /** The plan the wizard opens on. */
  plan?: UpgradeTier;
}

export function UpgradeCard({
  title,
  subtitle,
  media,
  tiles,
  compare,
  action,
  secondary,
  legal,
  className,
}: {
  title: string;
  subtitle?: string;
  /** A short demo above the tiles. */
  media?: ReactNode;
  tiles: PlanTile[];
  /** Without it, no plans table (it's the v4 catalog's). */
  compare?: UpgradeCardCompare;
  action: UpgradeCardAction;
  /** A second way in, e.g. "Book a demo", opened in a new tab. */
  secondary?: { label: string; href: string; onClick?: () => void };
  /** The fine print under the buttons (the trial's terms). */
  legal?: string;
  className?: string;
}) {
  const [compareOpen, setCompareOpen] = useState(false);
  // Two columns for a feature's four; three for a plan's five, six or eight
  // once the card has the room (two in a narrow panel or on a phone). Five and
  // eight leave the last row one short, so the compare tile fills it; without
  // the plans table, only a count of three's multiples goes three across.
  const threeUp = tiles.length > 4 && (!!compare || tiles.length % 3 === 0);
  const compareTile = !!compare && threeUp && tiles.length % 3 === 2;

  return (
    <div
      className={cn(
        'flex w-full flex-1 flex-col items-center justify-center px-4 py-10',
        className
      )}
    >
      <section className='@container w-full max-w-[600px] rounded-2xl border border-border bg-background p-8 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-12px_rgba(16,24,40,0.10)]'>
        <h2 className='text-xl font-semibold tracking-tight text-foreground'>
          {title}
        </h2>
        {subtitle ? (
          <p className='mt-1.5 text-[13px] leading-relaxed text-muted-foreground'>
            {subtitle}
          </p>
        ) : null}

        {media ? <div className='pt-6'>{media}</div> : null}

        {tiles.length > 0 ? (
          <div
            className={cn(
              'grid grid-cols-2 gap-2 pt-6',
              threeUp
                ? '@md:grid-cols-3'
                : // An odd one out takes the whole row.
                  '[&>:last-child:nth-child(odd)]:col-span-2'
            )}
          >
            {tiles.map((tile) => (
              <PlanTileView key={tile.label} tile={tile} />
            ))}
            {compareTile ? (
              <button
                type='button'
                onClick={() => setCompareOpen(true)}
                className={cn(
                  TILE,
                  'group hidden border border-dashed border-border bg-background transition-colors hover:border-foreground/25 hover:bg-muted/40 @md:flex'
                )}
              >
                <span className='flex h-[18px] items-center'>
                  <AppIcon
                    icon={iconArrowRight}
                    className='h-3.5 w-3.5 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground'
                  />
                </span>
                <span className='text-[13px] leading-snug font-medium text-muted-foreground group-hover:text-foreground'>
                  Compare all plans
                </span>
              </button>
            ) : null}
          </div>
        ) : null}

        {action.to ? (
          <Button asChild className='mt-6 h-10 w-full'>
            <Link
              to={action.to}
              search={action.search}
              onClick={action.onClick}
            >
              {action.label}
            </Link>
          </Button>
        ) : action.onClick ? (
          <Button
            type='button'
            onClick={action.onClick}
            disabled={action.busy}
            className='mt-6 h-10 w-full'
          >
            {action.busy ? <AppIcon icon={iconSpinnerThird} spin /> : null}
            {action.label}
          </Button>
        ) : (
          <Button asChild className='mt-6 h-10 w-full'>
            <UpgradeLink source='upgrade_card' plan={action.plan}>
              {action.label}
            </UpgradeLink>
          </Button>
        )}
        <div className='mt-1.5 flex gap-1.5'>
          {compare ? (
            <Button
              type='button'
              variant='ghost'
              onClick={() => setCompareOpen(true)}
              className={cn(
                'h-10 flex-1 rounded-[8px] text-sm font-medium text-muted-foreground hover:text-foreground',
                // The compare tile takes over where the grid is three across.
                compareTile && '@md:hidden'
              )}
            >
              Compare all plans
            </Button>
          ) : null}
          {secondary ? (
            <Button
              variant='ghost'
              asChild
              className='h-10 flex-1 rounded-[8px] text-sm font-medium text-muted-foreground hover:text-foreground'
            >
              <a
                href={secondary.href}
                target='_blank'
                rel='noopener noreferrer'
                onClick={secondary.onClick}
              >
                {secondary.label}
              </a>
            </Button>
          ) : null}
        </div>
        {legal ? (
          <p className='mt-3 text-center text-xs leading-5 text-muted-foreground'>
            {legal}
          </p>
        ) : null}
      </section>
      <div className='mt-5 w-full max-w-[600px] px-4 text-center text-xs leading-5 text-muted-foreground'>
        <TrialFooter />
      </div>
      {compare ? (
        <ComparePlansDialog
          plan={compare.plan}
          trial={compare.trial}
          open={compareOpen}
          onOpenChange={setCompareOpen}
        />
      ) : null}
    </div>
  );
}
